import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  Users,
  Trash2,
  Crown,
  Edit,
  Eye,
  Mail,
  Loader2,
  Link2,
  Check,
  Globe,
  Lock,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";

type MemberRole = "owner" | "editor" | "viewer";

interface Member {
  id: string;
  user_id: string;
  role: MemberRole;
  invited_at: string;
  accepted_at: string | null;
  email?: string;
  full_name?: string;
}

interface ShareCourseDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  courseId: string;
  courseName: string;
  isOwner: boolean;
  initialIsPublic: boolean;
  onPublicChanged?: (isPublic: boolean) => void;
}

const roleIcons: Record<MemberRole, React.ReactNode> = {
  owner: <Crown className="h-4 w-4 text-yellow-500" />,
  editor: <Edit className="h-4 w-4 text-blue-500" />,
  viewer: <Eye className="h-4 w-4 text-gray-500" />,
};

const roleLabels: Record<MemberRole, string> = {
  owner: "Owner",
  editor: "Editor",
  viewer: "Viewer",
};

export default function ShareCourseDialog({
  open,
  onOpenChange,
  courseId,
  courseName,
  isOwner,
  initialIsPublic,
  onPublicChanged,
}: ShareCourseDialogProps) {
  const [isPublic, setIsPublic] = useState(initialIsPublic);
  const [togglingPublic, setTogglingPublic] = useState(false);
  const [copied, setCopied] = useState(false);

  const [members, setMembers] = useState<Member[]>([]);
  const [loadingMembers, setLoadingMembers] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState<MemberRole>("viewer");
  const [inviting, setInviting] = useState(false);
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);

  const publicUrl = `${window.location.origin}/learn/courses/${courseId}`;

  useEffect(() => {
    setIsPublic(initialIsPublic);
  }, [initialIsPublic]);

  useEffect(() => {
    if (open) {
      fetchMembers();
      supabase.auth.getUser().then(({ data: { user } }) => setCurrentUserId(user?.id || null));
    }
  }, [open, courseId]);

  const handleTogglePublic = async (value: boolean) => {
    setTogglingPublic(true);
    const { error } = await supabase
      .from("learning_projects")
      .update({ is_public: value })
      .eq("id", courseId);

    if (error) {
      toast.error("Failed to update sharing settings");
    } else {
      setIsPublic(value);
      onPublicChanged?.(value);
      toast.success(value ? "Course is now public" : "Course is now private");
    }
    setTogglingPublic(false);
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(publicUrl).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const fetchMembers = async () => {
    setLoadingMembers(true);
    try {
      const { data, error } = await supabase
        .from("course_members")
        .select("*")
        .eq("course_id", courseId);

      if (error) throw error;

      const membersWithDetails = await Promise.all(
        (data || []).map(async (member: Member) => {
          const { data: profile } = await supabase
            .from("profiles")
            .select("email, full_name")
            .eq("id", member.user_id)
            .single();
          return { ...member, email: profile?.email || "Unknown", full_name: profile?.full_name || null };
        })
      );
      setMembers(membersWithDetails);
    } catch {
      toast.error("Failed to load members");
    } finally {
      setLoadingMembers(false);
    }
  };

  const handleInvite = async () => {
    if (!inviteEmail.trim()) {
      toast.error("Please enter an email address");
      return;
    }
    setInviting(true);
    try {
      const { data: profile, error: profileError } = await supabase
        .from("profiles")
        .select("id")
        .eq("email", inviteEmail.trim().toLowerCase())
        .single();

      if (profileError || !profile) {
        toast.error("User not found. They must have an account first.");
        return;
      }

      if (members.find((m) => m.user_id === profile.id)) {
        toast.error("This user is already a member");
        return;
      }

      const { data: { user } } = await supabase.auth.getUser();
      const { error: insertError } = await supabase.from("course_members").insert({
        course_id: courseId,
        user_id: profile.id,
        role: inviteRole,
        invited_by: user?.id,
        accepted_at: new Date().toISOString(),
      });

      if (insertError) throw insertError;

      toast.success(`Added ${inviteEmail} as ${roleLabels[inviteRole]}`);
      setInviteEmail("");
      setInviteRole("viewer");
      fetchMembers();
    } catch {
      toast.error("Failed to add member");
    } finally {
      setInviting(false);
    }
  };

  const handleUpdateRole = async (memberId: string, newRole: MemberRole) => {
    try {
      const { error } = await supabase
        .from("course_members")
        .update({ role: newRole })
        .eq("id", memberId);
      if (error) throw error;
      toast.success("Role updated");
      fetchMembers();
    } catch {
      toast.error("Failed to update role");
    }
  };

  const handleRemoveMember = async (memberId: string, memberEmail: string) => {
    if (!confirm(`Remove ${memberEmail} from this course?`)) return;
    try {
      const { error } = await supabase.from("course_members").delete().eq("id", memberId);
      if (error) throw error;
      toast.success("Member removed");
      fetchMembers();
    } catch {
      toast.error("Failed to remove member");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            Share "{courseName}"
          </DialogTitle>
          <DialogDescription>
            Make this course public for anyone with the link, or add specific members.
          </DialogDescription>
        </DialogHeader>

        {/* Public link section */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              {isPublic ? (
                <Globe className="h-4 w-4 text-green-600" />
              ) : (
                <Lock className="h-4 w-4 text-muted-foreground" />
              )}
              <div>
                <p className="text-sm font-medium">
                  {isPublic ? "Public — anyone with the link can enroll" : "Private — only members can access"}
                </p>
                <p className="text-xs text-muted-foreground">
                  {isPublic
                    ? "Learners sign up and are auto-enrolled when they open the link"
                    : "Turn on to generate a shareable public link"}
                </p>
              </div>
            </div>
            <Switch
              checked={isPublic}
              onCheckedChange={handleTogglePublic}
              disabled={togglingPublic || !isOwner}
            />
          </div>

          {isPublic && (
            <div className="flex gap-2">
              <Input value={publicUrl} readOnly className="text-xs text-muted-foreground bg-muted" />
              <Button variant="outline" size="sm" onClick={handleCopyLink} className="shrink-0 gap-1.5">
                {copied ? <Check className="h-4 w-4 text-green-600" /> : <Link2 className="h-4 w-4" />}
                {copied ? "Copied" : "Copy"}
              </Button>
            </div>
          )}
        </div>

        <Separator />

        {/* Member management */}
        <div className="space-y-3">
          <p className="text-sm font-medium">Or share with specific members</p>

          {isOwner && (
            <div className="space-y-2">
              <div className="flex gap-2">
                <Input
                  type="email"
                  placeholder="Enter email address"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  className="flex-1"
                />
                <Select value={inviteRole} onValueChange={(v) => setInviteRole(v as MemberRole)}>
                  <SelectTrigger className="w-28">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="viewer">
                      <div className="flex items-center gap-2">{roleIcons.viewer} Viewer</div>
                    </SelectItem>
                    <SelectItem value="editor">
                      <div className="flex items-center gap-2">{roleIcons.editor} Editor</div>
                    </SelectItem>
                  </SelectContent>
                </Select>
                <Button onClick={handleInvite} disabled={inviting}>
                  {inviting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />}
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">
                <strong>Editor:</strong> Can edit content · <strong>Viewer:</strong> Read-only access
              </p>
            </div>
          )}

          <div className="space-y-2 max-h-48 overflow-y-auto">
            {loadingMembers ? (
              <div className="flex items-center justify-center py-6">
                <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
              </div>
            ) : members.length === 0 ? (
              <p className="text-center text-sm text-muted-foreground py-4">
                No members yet. Add someone to collaborate!
              </p>
            ) : (
              members.map((member) => (
                <div key={member.id} className="flex items-center justify-between p-3 rounded-lg border bg-card">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-primary font-medium">
                      {(member.full_name || member.email || "?")[0].toUpperCase()}
                    </div>
                    <div>
                      <p className="font-medium text-sm">
                        {member.full_name || member.email}
                        {member.user_id === currentUserId && (
                          <Badge variant="outline" className="ml-2 text-xs">You</Badge>
                        )}
                      </p>
                      <p className="text-xs text-muted-foreground">{member.email}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {isOwner && member.user_id !== currentUserId ? (
                      <>
                        <Select
                          value={member.role}
                          onValueChange={(v) => handleUpdateRole(member.id, v as MemberRole)}
                        >
                          <SelectTrigger className="w-28">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="viewer">
                              <div className="flex items-center gap-2">{roleIcons.viewer} Viewer</div>
                            </SelectItem>
                            <SelectItem value="editor">
                              <div className="flex items-center gap-2">{roleIcons.editor} Editor</div>
                            </SelectItem>
                          </SelectContent>
                        </Select>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="text-destructive hover:text-destructive"
                          onClick={() => handleRemoveMember(member.id, member.email || "")}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </>
                    ) : (
                      <Badge variant="secondary" className="flex items-center gap-1">
                        {roleIcons[member.role]}
                        {roleLabels[member.role]}
                      </Badge>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
